import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { unwrapResult } from "@reduxjs/toolkit";
import RenewMotorLayout from "../renewalMotorPolicy/renewMotorLayout";
import TitleDescription from "../../../components/newMotorPolicy/titleDescription/titleDescription";
import VerifyOtpComponent from "../renewalMotorPolicy/verifyOtpPage/verifyOtpComponent";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";
import useTrackClaimFlow, { TRACK_CLAIM_LOGGED_OUT } from "../../../hooks/useTrackClaimFlow";
import {
  sendLoginOtp,
  verifyLoginOtp,
  selectLoginOtpLoading,
  selectLoginOtpPhoneNumber,
  selectLoginOtpPinId,
  selectLoginOtpError,
  clearLoginOtpState
} from "../../../redux/features/account/accountOtpSlice";
import { setLoginTokenInfo } from "../../../redux/features/loginRegisterSlice";
import { selectTrackClaimFormData } from "../../../redux/features/trackClaimFlowSlice";
import { getClaimDetails } from "../../../redux/features/crm/crmClaimSlice";
import { setLoggedInUserDataInLocalStorage } from "../../../config/config";
import CryptoJS from "crypto-js";

const TrackClaimLoginOtpPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const translate = useLanguageTranslate();
  const { handleFlowChange, handlePrevStep } = useTrackClaimFlow();

  const [otp, setOtp] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);

  const isLoading = useSelector(selectLoginOtpLoading);
  const phoneNumber = useSelector(selectLoginOtpPhoneNumber);
  const pinId = useSelector(selectLoginOtpPinId);
  const error = useSelector(selectLoginOtpError);
  const formData = useSelector(selectTrackClaimFormData);

  useEffect(() => {
    if (!pinId && formData?.cprNumber) {
      toast.error(
        translate(
          "Session expired. Please try again.",
          "انتهت الجلسة. يرجى المحاولة مرة أخرى."
        )
      );
      handleFlowChange(TRACK_CLAIM_LOGGED_OUT, 0);
    }
  }, [pinId, formData?.cprNumber]);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const encryptClaimNumber = (claimNumber) => {
    const secretKey = "i-hate-meej";
    const encryptedValue = CryptoJS.AES.encrypt(claimNumber, secretKey).toString();
    return encryptedValue
      .split("+").join("-")
      .split("/").join("_")
      .split("=").join("");
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 4) {
      toast.error(
        translate(
          "Please enter a valid 4-digit OTP",
          "يرجى إدخال رمز OTP مكون من 4 أرقام"
        )
      );
      return;
    }

    setIsVerifying(true);

    try {
      const verifyPayload = {
        CPR: formData.cprNumber,
        PinId: pinId,
        Pin: otp,
      };

      const verifyAction = await dispatch(verifyLoginOtp(verifyPayload));
      const verifyResult = unwrapResult(verifyAction);

      if (verifyResult) {
        dispatch(setLoginTokenInfo(verifyResult));
        setLoggedInUserDataInLocalStorage(verifyResult);

        const response = await dispatch(
          getClaimDetails({ 
            caseNumber: formData.claimNumber,
            token: verifyResult.access_token 
          })
        ).unwrap();

        if (response?.Claim) {
          const encryptedClaimNumber = encryptClaimNumber(response.Claim.CaseNumber);
          navigate(`/motor-insurance/claim-details/${encryptedClaimNumber}`);
        } else {
          toast.error(
            translate(
              "No claim found with this claim number.",
              "لم يتم العثور على مطالبة بهذا الرقم."
            )
          );
        }
      }
    } catch (e) {
      console.error(e);
      toast.error(
        translate(
          e?.ErrorMessageEn || "Verification failed",
          e?.ErrorMessageAr || "فشل التحقق"
        )
      );
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      setOtp("");
      const sendOtpPayload = {
        CPR: formData.cprNumber,
      };

      const sendOtpAction = await dispatch(sendLoginOtp(sendOtpPayload));
      unwrapResult(sendOtpAction);
      toast.success(
        translate("OTP sent successfully", "تم إرسال رمز OTP بنجاح")
      );
    } catch (e) {
      console.error(e);
      toast.error(
        translate(
          e?.ErrorMessageEn || "Failed to resend OTP",
          e?.ErrorMessageAr || "فشل إعادة إرسال رمز OTP"
        )
      );
    }
  };

  const handleGoBack = () => {
    dispatch(clearLoginOtpState());
    handlePrevStep();
  };

  const maskedPhone = phoneNumber ? `+973 ${phoneNumber}` : "+973 ********";

  return (
    <RenewMotorLayout
      show_close_btn
      is_hide_back_btn={false}
      btn_onClick={handleVerifyOtp}
      back_btn_onClick={handleGoBack}
      isLoading={isLoading || isVerifying}
      btn_disabled={otp.length !== 4 || isVerifying}
    >
      <div className="max-w-[100%] w-[500px] mx-auto bg-white max767:p-5 max767:pt-0">
        <TitleDescription
          title={translate("Verify OTP", "التحقق من رمز OTP")}
          description={translate(
            "Please enter the OTP sent to your registered mobile number.",
            "يرجى إدخال رمز OTP المرسل إلى رقم هاتفك المسجل."
          )}
        />
        <VerifyOtpComponent
          otp={otp}
          setOtp={setOtp}
          notRecivedCodeHandler={handleResendOtp}
          mobile={maskedPhone}
        />
      </div>
    </RenewMotorLayout>
  );
};

export default TrackClaimLoginOtpPage;
